/**
 * Keeps autoplay videos on TheraPrime pages actually playing.
 *
 * Correct markup is not enough on its own:
 *  - iOS Safari caps how many videos may decode at once, so a page with many
 *    videos silently refuses to start some of them. Playback is therefore gated
 *    on visibility: only videos in (or near) the viewport run.
 *  - Safari honours muting for autoplay purposes only when set from script,
 *    not from the `muted` attribute alone.
 *  - Low Power Mode blocks autoplay entirely until a user gesture occurs, so
 *    rejected plays are retried on the first tap/click/keypress.
 *
 * Only `video[autoplay]` is touched — videos meant to stay under user control
 * keep their own behaviour.
 */
(function () {
  'use strict';

  var SELECTOR = 'video[autoplay]';
  var pending = [];
  var gestureArmed = false;

  function noop() {}

  function prepare(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  }

  function retryPending() {
    var queue = pending.slice();
    pending.length = 0;
    gestureArmed = false;
    queue.forEach(play);
  }

  // A user gesture lifts the Low Power Mode autoplay block for later play() calls.
  function armGestureRetry(video) {
    if (pending.indexOf(video) === -1) pending.push(video);
    if (gestureArmed) return;
    gestureArmed = true;
    ['touchstart', 'click', 'keydown'].forEach(function (type) {
      document.addEventListener(type, retryPending, { once: true, passive: true });
    });
  }

  function play(video) {
    if (!video.isConnected) return;
    prepare(video);

    var attempt = video.play();
    if (!attempt || typeof attempt.catch !== 'function') return;

    attempt.catch(function () {
      // Either not enough data buffered yet, or autoplay is blocked outright.
      video.addEventListener(
        'canplay',
        function () {
          video.play().catch(function () {
            armGestureRetry(video);
          });
        },
        { once: true }
      );
      armGestureRetry(video);
    });
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                play(entry.target);
              } else if (!entry.target.paused) {
                entry.target.pause();
              }
            });
          },
          { rootMargin: '200px 0px', threshold: 0.01 }
        )
      : null;

  function register(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (video) {
      if (video.dataset.slAutoplayBound) return;
      video.dataset.slAutoplayBound = '1';
      prepare(video);
      if (observer) {
        observer.observe(video);
      } else {
        play(video);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      register();
    });
  } else {
    register();
  }

  // Sections re-render without a page load inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    register(event.target);
  });
})();
